import { Can } from "@/components/citadel";
import { useProject } from "@/hooks/use-project";
import type { ResourceLinkController } from "@/hooks/use-resource-link-controller";
import { AddResourceLinkModal } from "@/modals/projects/add-resource-link-modal";
import { Button } from "@msuite/ui/button";
import { useDisclosure } from "@msuite/ui/hooks";
import { Icon } from "@msuite/ui/icons";
import { useTranslation } from "@msuite/ui/localization";
import { TicketFormPendingResourceLinkItem } from "./ticket-form-pending-resource-link-item";

/** Props Interface */
interface TicketFormPendingResourceLinksProps {
	controller: ResourceLinkController;
	projectId: string | null;
}

export function TicketFormPendingResourceLinks({
	controller,
	projectId,
}: TicketFormPendingResourceLinksProps) {
	/** Hooks */
	const { t } = useTranslation();
	const disclosure = useDisclosure();

	/** Query */
	// The links staged here are written to `/projects/:projectId/resource-links` the moment the
	// ticket exists, so the rule that governs them is the same one the edit flow asks — otherwise a
	// caller who may not change the site stages links, the ticket is created, the links are refused,
	// and resubmitting the form creates the ticket a second time. The site is not chosen yet in
	// every add flow, and then the check is the category and the server has the last word.
	const { project } = useProject({ projectId });

	/** Render */
	return (
		<div className="v-stack gap-2">
			<div className="h-stack items-center justify-between">
				<p className="text-sm font-medium text-color">{t("resource_links.resource_links")}</p>
				<Can
					action="update"
					subject="Project"
					resource={project ? { ...project } : undefined}
				>
					<Button
						size="xxs"
						variant="white"
						icon={<Icon name="Plus" />}
						onClick={disclosure.onOpen}
						data-cy="ticket-add-resource-link-button"
					>
						{t("resource_links.add_resource_link")}
					</Button>
				</Can>
			</div>
			{controller.pendingLinks.map((pendingLink) => (
				<TicketFormPendingResourceLinkItem
					key={pendingLink.link}
					pendingLink={pendingLink}
					onRemove={controller.handleOnRemovePendingLink}
				/>
			))}
			{disclosure.isOpen && (
				<AddResourceLinkModal
					isOpen={disclosure.isOpen}
					onClose={disclosure.onClose}
					hideTicketPicker
					withoutPersistence
					onComplete={(json) =>
						controller.handleOnAddPendingLink({ link: json.link, name: json.name })
					}
				/>
			)}
		</div>
	);
}
